import { zodResolver } from '@hookform/resolvers/zod';
import CheckIcon from '@mui/icons-material/CheckCircle';
import LockResetIcon from '@mui/icons-material/LockReset';
import {
  Box,
  GlassAlert,
  GlassButton,
  Link,
  MagicInput,
  ShimmerLoader,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { auth } from '../config/firebase';

export default function ResetPasswordPage() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resetComplete, setResetComplete] = useState(false);
  const [email, setEmail] = useState<string | null>(null);

  const oobCode = searchParams.get('oobCode');

  const resetPasswordSchema = z
    .object({
      password: z.string().min(8, t('validation.minLength', { min: 8 })),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('validation.passwordMismatch'),
      path: ['confirmPassword'],
    });

  type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode) {
        setError(t('auth.resetPassword.invalidLink'));
        setIsVerifying(false);
        return;
      }

      try {
        const verifiedEmail = await verifyPasswordResetCode(auth, oobCode);
        setEmail(verifiedEmail);
      } catch (err) {
        setError(t('auth.resetPassword.expiredLink'));
        console.error('Reset code verification failed:', err);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyCode();
  }, [oobCode]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!oobCode) return;

    setIsLoading(true);
    setError(null);

    try {
      await confirmPasswordReset(auth, oobCode, data.password);
      setResetComplete(true);
    } catch (err) {
      const firebaseError = err as { code?: string };
      if (firebaseError.code === 'auth/expired-action-code') {
        setError(t('auth.resetPassword.expiredError'));
      } else if (firebaseError.code === 'auth/weak-password') {
        setError(t('auth.resetPassword.weakPassword'));
      } else {
        setError(t('auth.resetPassword.error'));
      }
      console.error('Password reset error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Build query string for navigation (preserve portal, tenant, redirect)
  const portal = searchParams.get('portal');
  const tenant = searchParams.get('tenant');
  // Accept both 'redirect' (common convention) and 'returnUrl' (legacy from tRPC clients)
  const redirect = searchParams.get('redirect') || searchParams.get('returnUrl');
  const params = new URLSearchParams();
  if (portal) params.set('portal', portal);
  if (tenant) params.set('tenant', tenant);
  if (redirect) params.set('redirect', redirect);
  const queryString = params.toString() ? `?${params.toString()}` : '';

  if (isVerifying) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <ShimmerLoader variant="circular" width={40} height={40} />
        </Box>
        <Typography variant="body1" color="textSecondary">
          {t('auth.resetPassword.verifying')}
        </Typography>
      </Box>
    );
  }

  if (resetComplete) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }} gutterBottom>
          {t('auth.resetPassword.successTitle')}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          {t('auth.resetPassword.successDescription')}
        </Typography>
        <RouterLink to={`/login${queryString}`} style={{ textDecoration: 'none' }}>
          <GlassButton variant="gradient">{t('auth.signIn')}</GlassButton>
        </RouterLink>
      </Box>
    );
  }

  if (error && !email) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <GlassAlert severity="error" visible={true} sx={{ mb: 3 }}>
          {error}
        </GlassAlert>
        <RouterLink to={`/forgot-password${queryString}`} style={{ textDecoration: 'none' }}>
          <GlassButton variant="gradient">{t('auth.resetPassword.requestNewLink')}</GlassButton>
        </RouterLink>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <LockResetIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }} gutterBottom>
          {t('auth.resetPassword.title')}
        </Typography>
        {email && (
          <Typography variant="body2" color="textSecondary">
            {t('auth.resetPassword.subtitle', { email })}
          </Typography>
        )}
      </Box>

      <GlassAlert severity="error" visible={!!error} sx={{ mb: 3 }}>
        {error}
      </GlassAlert>

      <form onSubmit={handleSubmit(onSubmit)}>
        <MagicInput
          {...register('password')}
          label={t('auth.resetPassword.newPassword')}
          type="password"
          autoComplete="new-password"
          error={!!errors.password}
          helperText={errors.password?.message}
          sx={{ mb: 2 }}
        />
        <MagicInput
          {...register('confirmPassword')}
          label={t('auth.resetPassword.confirmNewPassword')}
          type="password"
          autoComplete="new-password"
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
          sx={{ mb: 3 }}
        />

        <GlassButton type="submit" variant="subtle" fullWidth size="large" disabled={isLoading}>
          {isLoading ? (
            <ShimmerLoader variant="circular" width={24} height={24} />
          ) : (
            t('auth.resetPassword.resetButton')
          )}
        </GlassButton>
      </form>

      <Typography variant="body2" align="center" color="textSecondary" sx={{ mt: 3 }}>
        <Link component={RouterLink} to={`/login${queryString}`}>
          {t('auth.resetPassword.backToSignIn')}
        </Link>
      </Typography>
    </Box>
  );
}
